import { useCookie } from 'nuxt/app';

class Cookie {
  constructor(key, options = {}) {
    this.key = key;
    this.options = {
      domain: '',
      secure: false,
      sameSite: 'Lax',
      path: '/',
      ...options,
    };
  }
}

/**
 * Handles simple string-based cookies.
 */
export class SimpleCookie extends Cookie {
  get() {
    const cookie = useCookie(this.key, this.options);
    return cookie.value ?? '';
  }

  set(value, days) {
    const opts = { ...this.options };
    if (typeof days === 'number') {
      opts.maxAge = days * 24 * 60 * 60;
    }
    const cookie = useCookie(this.key, opts);
    cookie.value = value;
  }

  remove() {
    const cookie = useCookie(this.key, this.options);
    cookie.value = null;
  }

  static create(key, options) {
    return new SimpleCookie(key, options);
  }
}

/**
 * Handles boolean cookies, correctly interpreting various truthy values.
 */
export class BooleanCookie extends Cookie {
  get() {
    // Provide a default to ensure the type is correct from the start.
    const opts = { default: () => false, ...this.options };
    const cookie = useCookie(this.key, opts);
    // Handle various representations of boolean
    return [true, 'true', 1, '1'].includes(cookie.value);
  }

  set(value, days) {
    const opts = { ...this.options };
    if (typeof days === 'number') {
      opts.maxAge = days * 24 * 60 * 60;
    }
    const cookie = useCookie(this.key, opts);
    cookie.value = [true, 'true', 1, '1'].includes(value);
  }

  static create(key, options) {
    return new BooleanCookie(key, options);
  }
}

/**
 * Handles cookies that store arrays.
 */
export class ArrayCookie extends Cookie {
  get() {
    const opts = { default: () => [], ...this.options };
    const cookie = useCookie(this.key, opts);
    return cookie.value;
  }

  set(value, days) {
    const opts = { ...this.options };
    if (typeof days === 'number') {
      opts.maxAge = days * 24 * 60 * 60;
    }
    const cookie = useCookie(this.key, opts);
    cookie.value = Array.isArray(value) ? value : [];
  }

  push(...inputs) {
    const opts = { default: () => [], ...this.options };
    const cookie = useCookie(this.key, opts);
    cookie.value.push(...inputs);
  }

  removeByIndex(index) {
    const opts = { default: () => [], ...this.options };
    const cookie = useCookie(this.key, opts);
    if (index >= 0 && index < cookie.value.length) {
      cookie.value.splice(index, 1);
    }
  }

  reset() {
    this.set([]);
  }

  static create(key, options) {
    return new ArrayCookie(key, options);
  }
}

/**
 * Handles cookies that store a JSON object.
 */
export class JSONObjectCookie extends Cookie {
  get() {
    const opts = { default: () => ({}), ...this.options };
    const cookie = useCookie(this.key, opts);
    return cookie.value;
  }

  set(value, days) {
    const opts = { ...this.options };
    if (typeof days === 'number') {
      opts.maxAge = days * 24 * 60 * 60;
    }
    const cookie = useCookie(this.key, opts);
    cookie.value =
      typeof value === 'object' && value !== null && !Array.isArray(value) ? value : {};
  }

  getProperty(propKey) {
    const data = this.get();
    return data[propKey];
  }

  setProperty(propKey, value) {
    const opts = { default: () => ({}), ...this.options };
    const cookie = useCookie(this.key, opts);
    cookie.value[propKey] = value;
  }

  reset() {
    this.set({});
  }

  static create(key, options) {
    return new JSONObjectCookie(key, options);
  }
}
